import React, { useCallback, useEffect, useState } from "react";
import {
  Form,
  Input,
  Upload,
  Table,
  Button,
  Row,
  Col,
  Typography,
  Space,
  notification,
  Select,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  InboxOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { CaseDocumentDTO } from "../../models/CaseDocument";
import { caseDocumentService } from "../../services/caseDocumentService";
import { patientsService } from "../../services/patientsService";
import { PatientsDTO } from "../../models/Patients";
import { useLoaderProvider } from "../../contexts/LoaderContext";
import { documentTypes } from "../../config/constants";
import { useAuth } from "../../contexts/authContext";
import { isAslDoctor } from "../../config/utils";
import TitleTooltip from "../layout/TitleTooltip";

const { Dragger } = Upload;
const { Title, Paragraph } = Typography;
const { Option } = Select;

interface DocumentiProps {
  caseId: number;
  patient: PatientsDTO;
  onSave: () => void;
  setPatientPrivacy: (privacyConsens: boolean) => void;
}

const Documenti: React.FC<DocumentiProps> = ({
  patient,
  caseId,
  onSave,
  setPatientPrivacy,
}) => {
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState<CaseDocumentDTO[]>([]);
  const [privacyConsent, setPrivacyConsent] = useState("NEGATO");
  const { showLoader, hideLoader } = useLoaderProvider();
  const [isDraggerEnabled, setIsDraggerEnabled] = useState(false);
  const [isDisclaimerModalVisible, setIsDisclaimerModalVisible] =
    useState<boolean>(false);
  const { loggedDoctor, loggedUser } = useAuth();
  const isAsl = isAslDoctor(loggedUser!);
  const [tempFile, setTempFile] = useState<File | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await caseDocumentService.getAllCaseDocuments({
        page: 0,
        sort: ["uploadDate,desc"],
        "casesId.equals": caseId,
      });
      setDocuments(response);
    } catch (error) {
      console.error("Errore nel caricamento dei documenti:", error);
      notification.error({
        message: "Errore nel caricamento dei documenti",
      });
    }
  }, [caseId]);

  useEffect(() => {
    fetchDocuments();
    setPrivacyConsent(patient.privacyUploadDate ? "ACCETTATO" : "NEGATO");
  }, [caseId, fetchDocuments, patient.privacyUploadDate]);

  const columns = [
    {
      title: "Nome File",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Tipo Documento",
      dataIndex: "documentType",
      key: "documentType",
    },
    {
      title: "Data Caricamento",
      dataIndex: "uploadDate",
      key: "uploadDate",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Azioni",
      key: "actions",
      render: (text: string, record: CaseDocumentDTO) => (
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record.id!)}
          >
            Scarica
          </Button>
          {loggedDoctor?.id === record.doctor?.id && (
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id!)}
              danger
            >
              Elimina
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const showDisclaimerModal = (file: File) => {
    setTempFile(file);
    setIsDisclaimerModalVisible(true);
  };

  const handleDisclaimerOk = () => {
    setIsDisclaimerModalVisible(false);
    if (tempFile) {
      handleUploadConsent({ file: tempFile, onSuccess: () => { } });
    }
  };

  const handleDisclaimerCancel = () => {
    setIsDisclaimerModalVisible(false);
    setTempFile(null);
  };

  const handleUpload = async (info: any) => {
    const { status } = info.file;
    if (status === "done") {
      try {
        showLoader("Caricamento file...");
        const fileName = form.getFieldValue("fileName");
        const fileDescription = form.getFieldValue("fileDescription");
        await caseDocumentService.uploadCaseDocument(
          fileName,
          fileDescription,
          caseId,
          info.file.originFileObj,
        );
        notification.success({
          message: "File caricato con successo",
          description: `Il file ${fileName} è stato caricato con successo.`,
        });
        await fetchDocuments();
        form.resetFields();
      } catch (error) {
        console.error("Errore nel salvataggio del documento:", error);
        notification.error({
          message: "Errore nel salvataggio del documento",
        });
      } finally {
        hideLoader();
      }
    } else if (status === "error") {
      notification.error({
        message: "Errore nel caricamento del documento",
        description: `${info.file.name} caricamento fallito.`,
      });
    }
  };

  const handleDownload = async (documentId: number) => {
    try {
      showLoader("Download file...");
      const blob = await caseDocumentService.downloadCaseDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `document-${documentId}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download:", error);
      notification.error({
        message: "Errore durante il download",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async (documentId: number) => {
    try {
      showLoader("Eliminazione documento...");
      const deleteRes =
        await caseDocumentService.deleteCaseDocument(documentId);

      notification.success({
        message: "Documento eliminato con successo",
      });

      // bisogna aggiornare il paziente solo quando vengono tolti tutti i file di tipo privacy
      if (deleteRes.patientUpdated) {
        setPatientPrivacy && setPatientPrivacy(!deleteRes.patientUpdated);
        setPrivacyConsent("NEGATO");
      }

      await fetchDocuments();
    } catch (error) {
      console.error("Errore durante l'eliminazione:", error);
      notification.error({
        message: "Errore durante l'eliminazione del documento",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // Crea un link temporaneo per scaricare il file PDF
      const link = document.createElement("a");
      link.href = "/consenso_privacy.pdf";
      link.download = "consenso_privacy.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Download avviato",
        description:
          "Il file Consenso Privacy è stato avviato per il download.",
      });
    } catch (error) {
      console.error("Errore durante il download del template:", error);
      notification.error({
        message: "Errore durante il download",
        description: "Si è verificato un errore durante il download del file.",
      });
    }
  };

  const handleUploadConsent = async (info: any) => {
    const { file, onSuccess, onError } = info;

    try {
      showLoader("Caricamento consenso privacy...");
      await caseDocumentService.uploadCaseDocument(
        "Privacy Consent",
        "PRIVACY_CONSENT",
        caseId!,
        file,
      );

      // Update patient with privacy upload date
      const updatedPatient = {
        ...patient,
        privacyUploadDate: new Date().toISOString(),
      } as PatientsDTO;
      await patientsService.updatePatient(patient!.id!, updatedPatient);

      setPrivacyConsent("ACCETTATO");
      setPatientPrivacy && setPatientPrivacy(true);
      onSuccess("OK");
      notification.success({
        message: "Consenso privacy caricato con successo",
      });
      fetchDocuments();
    } catch (error) {
      console.error("Error uploading privacy consent:", error);
      onError(error);
      notification.error({
        message: "Errore durante il caricamento del consenso della privacy",
      });
    } finally {
      hideLoader();
    }
  };

  const handleFormChange = () => {
    const values = form.getFieldsValue();
    const isEnabled =
      values.fileName &&
      values.fileDescription &&
      privacyConsent === "ACCETTATO";
    setIsDraggerEnabled(isEnabled);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSave}
      onValuesChange={handleFormChange}
    >
      {/* Sezione Documento Privacy */}
      <TitleTooltip
        title="Documento Privacy*"
        titleColor="red"
        tooltipText="Obbligatorio"
        titleLevel={4}
        tooltipColor="#ff4d4f"
        titleStyle={{ fontWeight: "bold" }}
        className="mb-4"
      />
      <Row
        gutter={[16, 16]}
        justify="center"
        align="middle"
        style={{
          marginTop: "20px",
          marginBottom: "20px",
          display: "flex",
          flexWrap: "wrap",
          border: "1px solid red",
          padding: "30px",
        }}
      >
        <Col xs={24} sm={24} md={12}>
          <div
            style={{
              backgroundColor: "#f0f0f0",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <Space>
              <span>
                Autorizzazione al Trattamento dati personali e sensibili
              </span>
              <span
                style={{
                  backgroundColor:
                    privacyConsent === "NEGATO" ? "#ff4d4f" : "#52c41a",
                  color: "white",
                  padding: "2px 8px",
                  borderRadius: "4px",
                }}
              >
                {privacyConsent}
              </span>
            </Space>
          </div>
        </Col>
        <Col xs={24} sm={24} md={8}>
          {isAsl && (
            <Space direction="vertical">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadTemplate}
                style={{
                  backgroundColor: "#3E96FF",
                  borderColor: "#3E96FF",
                  color: "white",
                }}
              >
                Scarica Consenso Privacy
              </Button>
              <Upload
                accept=".pdf"
                showUploadList={false}
                beforeUpload={(file) => {
                  showDisclaimerModal(file);
                  return false; // Previene l'upload automatico
                }}
              >
                <Button
                  icon={<UploadOutlined />}
                  style={{
                    backgroundColor: "#FF4D4F",
                    borderColor: "#FF4D4F",
                    color: "white",
                  }}
                >
                  Carica Consenso Privacy
                </Button>
              </Upload>
            </Space>
          )}
        </Col>
      </Row>
      {/* Sezione Documenti del Caso Clinico */}
      <TitleTooltip
        title="Documenti del Caso Clinico"
        tooltipText="Inserire il nome del file ed il tipo prima di procedere al caricamento!"
        titleLevel={4}
        tooltipColor="#ff4d4f"
        titleStyle={{ fontWeight: "bold" }}
        className="mb-4"
      />
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="fileName"
            label="Nome File"
            rules={[{ required: true, message: "Inserisci il nome del file" }]}
          >
            <Input disabled={privacyConsent === "NEGATO"} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="fileDescription"
            label="Tipo Documento"
            rules={[
              { required: true, message: "Inserisci il tipo di documento" },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              disabled={privacyConsent === "NEGATO"}
            >
              {documentTypes.map((type) => (
                <Option key={type.value} value={type.value} label={type.label}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Form.Item>
        <Dragger
          disabled={!isDraggerEnabled}
          showUploadList={false}
          name="file"
          multiple={false}
          customRequest={({ file, onSuccess }) => {
            setTimeout(() => {
              onSuccess!({} as any);
            }, 0);
          }}
          onChange={handleUpload}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {isDraggerEnabled
              ? "Clicca o trascina il file per caricarlo"
              : "Compila Nome File e Tipo Documento per abilitare il caricamento"}
          </p>
          <p className="ant-upload-hint">
            Supporta il caricamento di un singolo file.
          </p>
        </Dragger>
      </Form.Item>

      <Title level={4} style={{ marginTop: "24px" }}>
        Documenti Caricati
      </Title>
      <Table
        columns={columns}
        dataSource={documents}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      />
      <Modal
        title="Disclaimer per l'upload del documento di privacy"
        visible={isDisclaimerModalVisible}
        onOk={handleDisclaimerOk}
        onCancel={handleDisclaimerCancel}
        okText="Accetto e Carico"
        cancelText="Annulla"
      >
        <Paragraph>
          Caricando questo documento di consenso privacy, lei conferma che:
        </Paragraph>
        <ul>
          <li>
            Il documento è stato correttamente compilato e firmato dal paziente.
          </li>
          <li>Ha l'autorizzazione necessaria per caricare questo documento.</li>
          <li>Il documento contiene informazioni veritiere e aggiornate.</li>
          <li>
            È consapevole che questo documento sarà associato al fascicolo del
            paziente.
          </li>
        </ul>
        <Paragraph strong>
          Procedendo con il caricamento, lei si assume la responsabilità della
          veridicità e dell'autenticità di questo documento di consenso privacy.
        </Paragraph>
      </Modal>
    </Form>
  );
};

export default Documenti;
